@let userValue = user.value();

@if (userValue && userValue.status === 'deleted') {
  <div class="p-6">
    <div class="rounded-lg border border-red-300 bg-red-50 p-6 max-w-2xl">
      <div class="flex items-center gap-3 mb-4">
        <i class="pi pi-trash text-red-600 text-2xl"></i>
        <h1 class="font-bold text-2xl text-red-800">User deleted</h1>
        <p-tag severity="danger">DELETED</p-tag>
      </div>
      <p class="text-sm text-red-700 mb-6">
        This user has been deleted. Only minimal tombstone information is retained.
      </p>
      <div class="grid grid-cols-1 gap-y-4">
        <div>
          <span class="text-sm text-muted-color">User ID</span>
          <div class="flex items-center gap-2">
            <span class="font-mono text-sm break-all">{{ userValue.id }}</span>
            <app-copy-button [value]="userValue.id"/>
          </div>
        </div>
        <div>
          <span class="text-sm text-muted-color">Email at deletion</span>
          <div class="flex items-center gap-2">
            <span class="text-sm break-all">{{ userValue.email ?? 'Unknown' }}</span>
            @if (userValue.email) {
              <app-copy-button [value]="userValue.email"/>
            }
          </div>
        </div>
        <div>
          <span class="text-sm text-muted-color">Deleted at</span>
          <div class="text-sm">
            {{ userValue.deletedAt ? (userValue.deletedAt | date: 'dd-MM-yyyy HH:mm:ss') : 'Unknown' }}
          </div>
        </div>
      </div>
    </div>
  </div>
}

@if (userValue && userValue.status !== 'deleted') {
  @let syncTag = syncProcessingTag();
  <div class="p-6">

    <h1 class="font-bold text-3xl">{{ userValue.id }}
      <p-tag [severity]="homeyStatusToTag(userValue.status)">{{ userValue.status }}</p-tag>
      @if (userValue.ignore) {
        <p-tag [severity]="'danger'">IGNORED</p-tag>
        <p-tag [severity]="ignoredReasonSeverity(userValue.ignoredReason)">
          {{ ignoredReasonLabel(userValue.ignoredReason) }}
        </p-tag>
      }
      @if (syncTag) {
        <p-tag [severity]="syncTag.severity">{{ syncTag.label }}</p-tag>
      }
    </h1>
    <div class="flex flex-row justify-between">
      <div class="max-w-2xl">
        <p class="text-sm text-muted-color mb-6">Created at {{ userValue.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</p>

        <div class="grid grid-cols-2 gap-y-6">
          <app-labeled label="First name" [value]="userValue.firstName"/>
          <app-labeled label="Last name" [value]="userValue.lastName"></app-labeled>

          <app-labeled label="Subscription status" [value]="userValue.subscription?.status"
                       [tagFunction]="subscriptionStatusToTag"></app-labeled>
          <app-labeled label="User app access" [value]="userValue.subscription?.paymentStatus"
                       [tagFunction]="paymentStatusToTag"></app-labeled>

          <app-labeled label="Last seen"
                       [value]="userValue.lastSeen | date: 'dd-MM-yyyy HH:mm:ss'"></app-labeled>
          <app-labeled label="Trialing" [value]="userValue.trialing ? 'Yes' : 'No'"></app-labeled>

          <app-labeled label="Email" [value]="userValue.email"></app-labeled>
          <app-labeled label="Phone" [value]="userValue.phone"></app-labeled>

          @let unsubscribeLinkValue = unsubscribeLink();
          @if (unsubscribeLinkValue) {
            <div class="col-span-2">
              <span class="text-sm text-muted-color">Unsubscribe link</span>
              <div class="flex items-center gap-2">
                <a [href]="unsubscribeLinkValue" target="_blank" class="text-primary underline break-all">{{ unsubscribeLinkValue }}</a>
                <app-copy-button [value]="unsubscribeLinkValue"/>
              </div>
            </div>
          }

          <app-labeled label="Household size" [value]="'' + (userValue.householdInfo?.members?.length ?? '')"></app-labeled>
          <app-labeled label="Rental assistance" [value]="userValue.rentalAssistance ? 'Yes' : 'No'"></app-labeled>
          <app-labeled label="Birth date" [value]="userValue?.birthDate"></app-labeled>

          <app-labeled label="Subscription type" [value]="userValue.subscription?.type"></app-labeled>
          <app-labeled label="Next payment date" [value]="userValue.subscription?.nextPaymentDate | date: 'dd-MM-yyyy'"></app-labeled>
          <app-labeled label="Referring source" [value]="userValue.source"/>
          <app-labeled label="HC Profile Status" [value]="userValue.housingConnectProfile?.status"
                       [tagFunction]="nycProfileStatusToTag"></app-labeled>
          <app-labeled label="HC Last Sync Status" [value]="userValue.housingConnectProfile?.lastSyncStatus"
                       [tagFunction]="lastSyncStatusToTag"></app-labeled>
          <app-labeled label="HC Login Status" [value]="userValue.housingConnectProfile?.loginStatus"
                       [tagFunction]="loginStatusToTag"></app-labeled>
          <app-labeled label="HC Login Failed Count" [value]="'' + (userValue.housingConnectProfile?.loginFailedCount ?? 0)"
                       [tagFunction]="loginFailedCountToTag"></app-labeled>
          <app-labeled label="HC Sync Failed Count" [value]="'' + (userValue.housingConnectProfile?.syncFailedCount ?? 0)"
                       [tagFunction]="syncFailedCountToTag"></app-labeled>
          <app-labeled label="HC Last synced at"
                       [value]="userValue.housingConnectProfile?.lastSyncedAt | date: 'dd-MM-yyyy HH:mm:ss'"></app-labeled>
          <app-labeled label="HC Last sync considered at"
                       [value]="userValue.housingConnectProfile?.lastSyncConsideredAt | date: 'dd-MM-yyyy HH:mm:ss'"></app-labeled>
          <app-labeled label="HC Last sync error" [value]="userValue.housingConnectProfile?.lastSyncError"/>
          <app-labeled label="HC Ignored" [value]="userValue.housingConnectProfile?.ignore ? 'Yes' : 'No'"></app-labeled>
          <app-labeled label="HC Ignored reason" [value]="userValue.housingConnectProfile?.ignoredReason"></app-labeled>
          <app-labeled label="HC Suppress unknown emails" [value]="userValue.housingConnectProfile?.suppressUnknownEmails ? 'Yes' : 'No'"></app-labeled>

          <app-labeled label="HC Email 1" [value]="userValue.housingConnectProfile?.email1"></app-labeled>
          <app-labeled label="HC Email 2" [value]="userValue.housingConnectProfile?.email2"></app-labeled>
          <app-labeled label="HC Username" [value]="userValue.housingConnectProfile?.username"></app-labeled>
          <app-labeled label="HC Password" [value]="userValue.housingConnectProfile?.password" [copy]="true"></app-labeled>
        </div>
      </div>
      <div class="flex flex-col gap-2 mt-12 sticky top-6 self-start max-h-[calc(100vh-4rem)] overflow-y-auto">
        <p-button
          (onClick)="onRegisterInNYCClick()"
          label="Register in NYC"
          icon="pi pi-plus"
          [disabled]="userValue.housingConnectProfile?.status === 'complete'"/>
        @if (userValue.housingConnectProfile?.status === 'waiting-on-email2-verification') {
          <p-button
            (onClick)="unlockEmailTwo()"
            label="Unlock Email 2 State"
            icon="pi pi-mail"/>
        }
        @if (userValue.householdInfo && userValue.housingConnectProfile?.status === 'complete') {
          <p-button
            (onClick)="verifyHousehold()"
            label="Verify NYC household"
            icon="pi pi-check"
            size="small"
            [loading]="verifyingHousehold()"
            [disabled]="verifyingHousehold()"/>
        }
        @if (userValue.status === 'active') {
          <p-button
            (onClick)="onIgnoreToggle()"
            label="Toggle ignore"
            icon="pi pi-pencil"
            [loading]="togglingIgnore()"
            [disabled]="togglingIgnore()"/>
          <p-button
            (onClick)="toggleSuppressUnknownEmails()"
            [label]="userValue.housingConnectProfile?.suppressUnknownEmails ? 'Enable unknown emails' : 'Suppress unknown emails'"
            [icon]="userValue.housingConnectProfile?.suppressUnknownEmails ? 'pi pi-bell' : 'pi pi-bell-slash'"
            [severity]="userValue.housingConnectProfile?.suppressUnknownEmails ? 'warn' : 'secondary'"
            size="small"
            [loading]="togglingSuppressUnknownEmails()"
            [disabled]="togglingSuppressUnknownEmails() || userValue.housingConnectProfile?.status !== 'complete'"/>
        @if (allowManualNycSync()) {
          <p-button
            (onClick)="syncUserWithNyc()"
            label="Manual NYC sync"
            icon="pi pi-sync"
            [loading]="syncingUserWithNyc()"
            [disabled]="syncingUserWithNyc()"/>
        }
        <p-button
          (onClick)="fixNycProfile()"
          label="Fix NYC profile"
          icon="pi pi-wrench"
          size="small"
          [loading]="fixingNycProfile()"
          [disabled]="fixingNycProfile() || !(userValue.status === 'active' && userValue.housingConnectProfile?.status === 'complete')"/>
        <p-button
          (onClick)="addFirstHouseholdMember()"
          label="Add first household member"
          icon="pi pi-user-plus"
          size="small"
          [loading]="addingFirstHouseholdMember()"
          [disabled]="addingFirstHouseholdMember() || !(userValue.status === 'active' && userValue.housingConnectProfile?.status === 'complete')"/>
        <p-button
          (onClick)="redistributeIncome()"
          label="Redistribute income"
          icon="pi pi-dollar"/>
          <p-button
            (onClick)="resetLoginSyncResult()"
            label="Resey login sync result"
            icon="pi pi-user"/>
          <p-button
            (onClick)="markCurrentProfileComplete()"
            label="Mark profile complete"
            icon="pi pi-refresh"/>
          <p-button
            (onClick)="allowResetPassword()"
            label="Mark resetting password"
            icon="pi pi-user"/>
          <p-button
            (onClick)="editNycAuth()"
            label="Edit NYC auth"
            icon="pi pi-lock"
            [disabled]="!(userValue.housingConnectProfile?.status === 'not-created' || userValue.housingConnectProfile?.status === 'complete')"/>
          <p-button
            (onClick)="sendTextMessage()"
            label="Send text message"
            icon="pi pi-comment"
            [disabled]="!(userValue.smsNotificationsEnabled && userValue.phone)"/>
          @if (allowApplicationUpsert) {
            <p-button
              (onClick)="openApplicationCreatePage()"
              label="Add application"
              icon="pi pi-file-plus"/>
          }
        }
        <p-button
          (onClick)="linkAuthToOtherUser()"
          label="Link auth to other user"
          icon="pi pi-link"/>
        @if (!userValue.subscription || userValue.subscription.paymentStatus === 'not-paid') {
          <p-button
            (onClick)="markAsPaid()"
            label="Mark as paid"
            icon="pi pi-dollar"/>
        }
        @if (allowStripeStatusUpdate && userValue.subscription?.type === 'stripe') {
          <p-button
            (onClick)="openStripeStatusDialog()"
            label="Update Stripe status"
            icon="pi pi-pencil"/>
        }
        <p-button
          (onClick)="openNotificationTestPage()"
          label="Test notification"
          icon="pi pi-bell"/>
        <p-button
          (onClick)="openInAppNotificationTestPage()"
          label="Test in-app notification"
          icon="pi pi-inbox"/>
        <p-button
          (onClick)="deleteUser()"
          label="Delete"
          severity="danger"
          icon="pi pi-trash"
        />
      </div>
    </div>

    <div class="mt-8">
      <h2 class="text-xl font-medium mb-3">Connected accounts</h2>
      <div class="border surface-card rounded-md p-4">
        @if (userValue.connectedAccounts?.length) {
          <div class="flex flex-col gap-3">
            @for (account of userValue.connectedAccounts ?? []; track account.authId) {
              <div class="flex flex-wrap items-center justify-between gap-4">
                <div class="flex flex-col gap-1">
                  <span class="font-mono text-sm text-surface-700">{{ account.authId }}</span>
                  <span class="text-sm text-muted-color">{{ account.username ?? 'No username stored' }}</span>
                </div>
                <p-tag [severity]="connectedAccountSeverity(account.provider)">
                  {{ connectedAccountProviderLabel(account.provider) }}
                </p-tag>
              </div>
            }
          </div>
        } @else {
          <p class="text-sm text-muted-color">No connected accounts recorded.</p>
        }
      </div>
    </div>

    <h2 class="mt-12 text-xl font-medium">Mails</h2>
    <p-table [value]="userValue.mails" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>Id</th>
          <th>Status</th>
          <th>Subject</th>
          <th>Timestamp</th>
          <th>Error message</th>
          <th>Download</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-email [cTypedTableRow]="userValue.mails">
        <tr>
          <td>{{ email.id }}</td>
          <td>
            <p-tag>{{ email.status }}</p-tag>
          </td>
          <td>{{ email.recipient }}</td>
          <td>{{ email.subject }}</td>
          <td>
            {{ email.timestamp | date: 'dd-MM-yyyy HH:mm:ss' }}
          </td>
          <td>
            @if (email.status !== 'processed') {
              <p-tag>{{ email.errorMessage }}</p-tag>
            }
          </td>
          <td>
            <p-button (onClick)="onEmailDownload(email.s3Key)">Download</p-button>
            <p-button (onClick)="retryEmail(email.s3Key, email.recipient)">Retry</p-button>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6" class="text-center">No emails found</td>
        </tr>
      </ng-template>
    </p-table>
    <h2 class="mt-12 text-xl font-medium">Applications</h2>
    <div class="mb-6 mt-2 flex flex-wrap gap-2 items-center">
      <p-button class="mr-4" (onClick)="getEligibility()">Check eligibility for Apartment</p-button>
      <p-button (onClick)="addApartmentManually()">Add apartment from Housing Connect</p-button>
      <p-button
        label="Resync all failed"
        icon="pi pi-refresh"
        size="small"
        severity="secondary"
        (onClick)="resyncAllFailedApplications()"
        [disabled]="!hasFailedApplications()"></p-button>
      @if (allowEligibleLotteryEvent) {
        <p-button
          class="ml-4"
          (onClick)="openEligibleEventDialog()"
          label="Emit eligible lottery event"
          icon="pi pi-bolt"></p-button>
      }
    </div>
    @if (eligibilityCheckResult(); as eligibility) {
      <div class="mb-6 space-y-4">
        <div class="text-lg font-semibold text-neutral-900">Eligibility check</div>
        <div class="grid gap-4 md:grid-cols-2">
          <div class="rounded border border-neutral-200 bg-white p-4 shadow-sm space-y-3">
            <div class="text-xs font-semibold uppercase tracking-wide text-neutral-500">Result</div>
            <p-tag [severity]="eligibility.result === 'yes' ? 'success' : 'warn'">{{ eligibility.result }}</p-tag>
            <div class="space-y-3 text-sm text-neutral-600">
              @if (eligibility.eligibilityData) {
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <div class="text-xs text-neutral-400">Lower income</div>
                    <div class="font-semibold">${{ eligibility.eligibilityData.lowerHouseholdIncome | number:'1.0-0' }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-neutral-400">Upper income</div>
                    <div class="font-semibold">${{ eligibility.eligibilityData.upperHouseholdIncome | number:'1.0-0' }}</div>
                  </div>
                </div>
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <div class="text-xs text-neutral-400">Household size</div>
                    <div class="font-semibold">{{ eligibility.eligibilityData.size }}</div>
                  </div>
                  <div>
                    <div class="text-xs text-neutral-400">Rental assistance</div>
                    <div class="font-semibold">{{ eligibility.eligibilityData.receivesRentalAssistance ? 'Yes' : 'No' }}</div>
                  </div>
                </div>
                <div>
                  <div class="text-xs text-neutral-400">Eligible for senior</div>
                  <div class="font-semibold">{{ eligibility.eligibilityData.eligibleForSenior ? 'Yes' : 'No' }}</div>
                </div>
              } @else {
                <div class="text-sm text-neutral-500">There was no household info to derive the eligibility input.</div>
              }
            </div>
          </div>
          <div class="md:col-span-2 space-y-2">
            <div class="text-xs font-semibold uppercase tracking-wide text-neutral-500">Eligible units (JSON)</div>
            <pre class="max-h-48 overflow-auto rounded border border-neutral-200 bg-slate-50 p-3 text-xs leading-relaxed whitespace-pre-wrap">{{ eligibility.eligibleUnits | json }}</pre>
            @if (eligibility.eligibleUnits.length === 0) {
              <div class="text-xs text-neutral-500">No lottery units matched the eligibility criteria.</div>
            }
          </div>
        </div>
      </div>
    }
    <p-table [value]="userValue.applications" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>Apartment Id</th>
          <th>Status</th>
          <th>Method</th>
          <th>Created At</th>
          <th>Log Nr</th>
          <th>NYC Override</th>
          <th>NYC Status</th>
          <th>Withdrawn</th>
          <th>Failure Reason</th>
          <th>Id</th>
          <th>Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-application [cTypedTableRow]="userValue.applications">
        <tr>
          <td>{{ application.lotteryId }}</td>
          <td>
            <p-tag [severity]="applicationStatusToTag(application.status)">{{ application.status }}</p-tag>
          </td>
          <td>{{ application.method }}</td>
          <td>{{ application.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>{{ application.logNr }}</td>
          <td>{{ getNycApplicationOverrideSummary(application.id) }}</td>
          <td>{{ application.nycStatusLabel }}</td>
          <td>{{ application.withdrawnManually }}</td>
          <td>{{ application.failureReason }}</td>
          <td>{{ application.id }}</td>

          <td>
            <div class="flex flex-col gap-2">
              @if (application.status === 'not-synced' || application.status === 'failed') {
                <p-button size="small" (onClick)="deleteApplication(application.id)" label="Delete"
                          icon="pi pi-trash"></p-button>
              }
              @if (application.status === 'failed') {
                <p-button size="small" (onClick)="resyncApplication(application.id)" label="Resync"
                          icon="pi pi-refresh"></p-button>
                <p-button size="small" (onClick)="markApplicationAsNotSelected(application.id)" label="Fail"
                          icon="pi pi-send"></p-button>
              }
              @if (allowApplicationStatusEvent) {
                <p-button
                  size="small"
                  (onClick)="openStatusEventDialog(application)"
                  label="Emit status event"
                  icon="pi pi-bolt"></p-button>
              }
              @if (allowNycApplicationOverrides) {
                <p-button
                  size="small"
                  (onClick)="openNycApplicationOverrideDialog(application)"
                  label="NYC override"
                  icon="pi pi-sliders-h"></p-button>
                @if (getNycApplicationOverride(application.id)) {
                  <p-button
                    size="small"
                    severity="secondary"
                    (onClick)="clearNycApplicationOverride(application.id)"
                    label="Clear override"
                    icon="pi pi-times"></p-button>
                }
              }
            </div>

          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="11" class="text-center">No applications found</td>
        </tr>
      </ng-template>
    </p-table>
    <h2 class="mt-12 text-xl font-medium">Notification channels</h2>
    <div class="grid gap-4 mt-4 md:grid-cols-3">
      <div class="p-4 border surface-card rounded-md">
        <div class="flex justify-between items-center">
          <span class="font-semibold">Email channel</span>
          <p-tag
            [severity]="userValue.notificationChannels.email.enabled ? 'success' : 'danger'"
          >{{ userValue.notificationChannels.email.enabled ? 'Enabled' : 'Disabled' }}</p-tag>
        </div>
        <p class="text-sm mt-2 text-muted-color">
          Address: {{ userValue.notificationChannels.email.emailAddress || 'Not configured' }}
        </p>
      </div>
      <div class="p-4 border surface-card rounded-md">
        <div class="flex justify-between items-center">
          <span class="font-semibold">SMS channel</span>
          <p-tag
            [severity]="userValue.notificationChannels.text.enabled && !userValue.notificationChannels.text.optedOut ? 'success' : 'danger'"
          >{{ userValue.notificationChannels.text.enabled && !userValue.notificationChannels.text.optedOut ? 'Enabled' : 'Disabled' }}</p-tag>
        </div>
        <p class="text-sm mt-2 text-muted-color">
          Phone: {{ userValue.notificationChannels.text.phoneNr || 'Not configured' }}
        </p>
        <p class="text-sm text-muted-color">
          Opted out: {{ userValue.notificationChannels.text.optedOut ? 'Yes' : 'No' }}
        </p>
      </div>
      <div class="p-4 border surface-card rounded-md">
        <div class="flex justify-between items-center">
          <span class="font-semibold">Push channel</span>
          <p-tag
            [severity]="userValue.notificationChannels.push.enabled ? 'success' : 'danger'"
          >{{ userValue.notificationChannels.push.enabled ? 'Enabled' : 'Disabled' }}</p-tag>
        </div>
        <p class="text-sm mt-2 text-muted-color">
          Tokens: {{ userValue.notificationChannels.push.tokens.length }}
        </p>
      </div>
    </div>
    @if (userValue.notificationCategoryPreferences) {
      <h2 class="mt-12 text-xl font-medium">Notification category preferences</h2>
      <div class="grid gap-4 mt-4 md:grid-cols-4">
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Core</span>
            <p-tag
              [severity]="userValue.notificationCategoryPreferences.core ? 'success' : 'danger'"
            >{{ userValue.notificationCategoryPreferences.core ? 'Enabled' : 'Disabled' }}</p-tag>
          </div>
        </div>
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Promotional</span>
            <p-tag
              [severity]="userValue.notificationCategoryPreferences.promotional ? 'success' : 'danger'"
            >{{ userValue.notificationCategoryPreferences.promotional ? 'Enabled' : 'Disabled' }}</p-tag>
          </div>
        </div>
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Lottery</span>
            <p-tag
              [severity]="userValue.notificationCategoryPreferences.lottery ? 'success' : 'danger'"
            >{{ userValue.notificationCategoryPreferences.lottery ? 'Enabled' : 'Disabled' }}</p-tag>
          </div>
        </div>
        <div class="p-4 border surface-card rounded-md">
          <div class="flex justify-between items-center">
            <span class="font-semibold">Lottery follow-up</span>
            <p-tag
              [severity]="userValue.notificationCategoryPreferences.lotteryfollowup ? 'success' : 'danger'"
            >{{ userValue.notificationCategoryPreferences.lotteryfollowup ? 'Enabled' : 'Disabled' }}</p-tag>
          </div>
        </div>
      </div>
    }
  <h2 class="mt-12 text-xl font-medium">Notifications</h2>
  <p-table [value]="userValue.notifications" [scrollable]="true" [scrollHeight]="tableScrollHeight">
    <ng-template pTemplate="header">
      <tr>
        <th>Type</th>
        <th>Created at</th>
        <th>Send status</th>
        <th></th>
      </tr>
    </ng-template>
      <ng-template pTemplate="body" let-notification [cTypedTableRow]="userValue.notifications">
        <tr>
          <td>{{ notification.type }}</td>
          <td>{{ notification.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>{{ notification.readStatus }}</td>
          <td>
            <div class="flex flex-col gap-1">
              @for (status of notification.sendStatuses; track status) {
                <div class="text-sm">
                  {{ status.channel }}: {{ status.sendStatus }}
                  @if (status.failureReason) {
                    <span class="text-muted-color">
                      (failure: {{ status.failureReason }})
                    </span>
                  }
                  @if (status.notNeededReason) {
                    <span class="text-muted-color">
                      (not needed: {{ status.notNeededReason }})
                    </span>
                  }
                </div>
              }
              @if (!notification.sendStatuses.length) {
                <span class="text-muted-color">-</span>
              }
            </div>
          </td>
          <td>
            <p-button severity="secondary" [text]="true" size="small" label="Resend"
                    (onClick)="resendNotification(userValue.id, notification.id)"/>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" class="text-center">No notifications found</td>
        </tr>
      </ng-template>
    </p-table>
    <h2 class="mt-12 text-xl font-medium">In-app notifications</h2>
    <p-table [value]="userValue.inAppNotifications" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>Type</th>
          <th>Status</th>
          <th>Created at</th>
          <th>Read at</th>
          <th>Data</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-notification [cTypedTableRow]="userValue.inAppNotifications">
        <tr>
          <td>{{ notification.type }}</td>
          <td>
            <p-tag [severity]="notification.status === 'read' ? 'success' : 'warn'">
              {{ notification.status }}
            </p-tag>
          </td>
          <td>{{ notification.createdAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>{{ notification.readAt | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td><pre class="text-xs whitespace-pre-wrap">{{ notification.data | json }}</pre></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="5" class="text-center">No in-app notifications found</td>
        </tr>
      </ng-template>
    </p-table>
    <h2 class="mt-12 text-xl font-medium">
      Domain events
      @if (userValue.domainEventsTruncated) {
        <span class="text-base font-normal text-gray-500">newest {{ userValue.domainEvents.length }} shown</span>
      }
    </h2>
    <p-table [value]="userValue.domainEvents" [scrollable]="true" [scrollHeight]="tableScrollHeight">
      <ng-template pTemplate="header">
        <tr>
          <th>ID</th>
          <th>Type</th>
          <th>Timestamp</th>
          <th>Payload</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-event [cTypedTableRow]="userValue.domainEvents">
        <tr>
          <td>{{ event.id }}</td>
          <td>{{ event.eventType }}</td>
          <td>{{ event.timestamp | date: 'dd-MM-yyyy HH:mm:ss' }}</td>
          <td>
            <pre class="text-xs whitespace-pre-wrap">{{ event.payload | json }}</pre>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" class="text-center">No domain events found</td>
        </tr>
      </ng-template>
    </p-table>
  </div>
  @if (userValue.householdInfo) {
    <div class="p-8">
      <h2 class="text-2xl font-medium mb-4">Household</h2>
      <div class="flex flex-row gap-8 mb-6">
        <app-labeled label="Sync status"
                     [value]="userValue.householdInfo.nycData.syncStatus"
                     [tagFunction]="syncStatusToTag"></app-labeled>
        @if (userValue.householdInfo.nycData.syncStatus === 'sync-failed') {
          <p-button size="small"
                    (onClick)="reconnectHousehold()">Re-connect household id
          </p-button>
        }
      </div>


      @if (householdVerificationResult(); as verification) {
        <div class="mt-4 border border-surface-200 rounded p-4">
          <div class="flex items-center gap-3 mb-2">
            <span class="font-medium">Last verification</span>
            <p-tag
              [severity]="verification.status === 'ok'
                ? (verification.issues.length === 0 && verification.householdComplete ? 'success' : 'warn')
                : 'danger'">
              {{ verification.status }}
            </p-tag>
            @if (verification.status === 'ok') {
              <span class="text-sm text-surface-500">Household id: {{ verification.householdId ?? 'none' }}</span>
            }
          </div>

          @if (verification.status === 'ok') {
            <p class="text-sm mb-2">Complete in NYC: {{ verification.householdComplete ? 'Yes' : 'No' }}</p>
            @if (verification.issues.length === 0) {
              <p class="text-sm text-green-700">No verification issues found.</p>
            } @else {
              <ul class="list-disc list-inside text-sm text-orange-700">
                @for (issue of verification.issues; track issue.code) {
                  <li>{{ issue.message }}</li>
                }
              </ul>
            }
          } @else {
            <p class="text-sm text-orange-700">{{ verification.reason }}</p>
          }
        </div>
      }

      @for (member of userValue.householdInfo.members; track member) {
        <app-household-member-details [householdMember]="member"></app-household-member-details>
      }

    </div>
  }
}

<p-dialog header="Register in NYC" [modal]="true" [visible]="nycAuth() !== undefined"
          (visibleChange)="!nycAuth() || nycAuth.set(undefined)" [style]="{ width: '50rem' }">
  @if (nycAuth(); as nycAuth) {
    <div class="flex flex-row justify-between items-center mb-6">
    <span class="p-text-secondary block">
      NYC Profile Information
    </span>
    </div>
    <div class="flex items-center gap-4 mb-4">
      <div class="font-semibold w-24">Email 1</div>
      <div>{{ nycAuth.email1 }}</div>
    </div>
    <div class="flex items-center gap-4 mb-4">
      <div class="font-semibold w-24">Email 2</div>
      <div>{{ nycAuth.email2 }}</div>
    </div>
    <div class="flex items-center gap-4 mb-4">
      <div class="font-semibold w-24">Username</div>
      <div>{{ nycAuth.username }}</div>
    </div>
    <div class="flex items-center gap-4 mb-8">
      <div class="font-semibold w-24">Password</div>
      <div>{{ nycAuth.password }}</div>
    </div>
  }
</p-dialog>
<p-dialog
  header="Emit new-eligible-lottery event"
  [modal]="true"
  [visible]="eligibleEventDialogVisible()"
  (visibleChange)="eligibleEventDialogVisible.set($event)"
  [style]="{ width: '32rem' }">
  <p class="text-sm text-orange-600 mb-4">
    Test env only. Sends new-eligible-lottery event(s) for user {{ userId() }}.
  </p>

  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap gap-2">
      @for (lottery of displayOpenLotteries(); track lottery.id) {
        <p-button
          [label]="lottery.name || lottery.id"
          size="small"
          [severity]="selectedLotteryIds().has(lottery.id) ? 'primary' : 'secondary'"
          (onClick)="toggleLotterySelection(lottery.id)"></p-button>
      }
    </div>

    <div class="flex flex-wrap gap-2 items-center">
      <p-button
        label="Random open lottery"
        icon="pi pi-refresh"
        size="small"
        severity="secondary"
        (onClick)="selectRandomOpenLottery()"></p-button>
      @if (selectedLotteryIds().size > 0) {
        <span class="text-sm text-muted-color">
          Selected: {{ selectedLotteryLabel() }}
        </span>
      }
    </div>
  </div>

  <div class="flex gap-2 justify-end mt-4">
    <p-button label="Cancel" severity="secondary" (onClick)="eligibleEventDialogVisible.set(false)"></p-button>
    <p-button label="Emit event" icon="pi pi-bolt" (onClick)="emitEligibleLotteryEvents()"></p-button>
  </div>
</p-dialog>
<p-dialog
  header="Emit application status event"
  [modal]="true"
  [visible]="statusEventDialogVisible()"
  (visibleChange)="statusEventDialogVisible.set($event)"
  [style]="{ width: '32rem' }">
  @if (selectedApplication(); as application) {
    <p class="text-sm text-orange-600 mb-4">
      Test env only. Sends an application-status-changed event for {{ application.id }} (lottery {{ application.lotteryId }}).
    </p>

    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap gap-2">
        @for (status of statusOptions; track status) {
          <p-button
            [label]="status"
            [severity]="statusEventForm.controls.status.value === status ? 'primary' : 'secondary'"
            size="small"
            (onClick)="setStatus(status)"></p-button>
        }
      </div>

      @if (statusEventForm.controls.status.value === 'applied') {
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold">Application method</span>
          <div class="flex flex-wrap gap-2">
            @for (method of applicationMethodOptions; track method) {
              <p-button
                [label]="method"
                size="small"
                [severity]="statusEventForm.controls.applicationMethod.value === method ? 'primary' : 'secondary'"
                (onClick)="setApplicationMethod(method)"></p-button>
            }
            <p-button
              label="Random"
              icon="pi pi-refresh"
              size="small"
              severity="secondary"
              (onClick)="setApplicationMethod(randomApplicationMethod())"></p-button>
          </div>
        </div>
      }

      @if (statusEventForm.controls.status.value === 'pending-selection' || statusEventForm.controls.status.value === 'not-selected') {
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold">Lottery number</span>
          <div class="flex flex-wrap gap-2">
            <p-button
              label="Use log number"
              size="small"
              severity="secondary"
              (onClick)="setLotteryNumberFromLog(application)"></p-button>
            <p-button
              label="Random"
              size="small"
              icon="pi pi-refresh"
              severity="secondary"
              (onClick)="setLotteryNumberRandom()"></p-button>
            @if (statusEventForm.controls.lotteryNumber.value) {
              <span class="text-sm text-muted-color">
                Selected: {{ statusEventForm.controls.lotteryNumber.value }}
              </span>
            }
          </div>
        </div>
      }

      @if (statusEventForm.controls.status.value === 'not-selected') {
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold">Reason</span>
          <div class="flex flex-wrap gap-2">
            @for (reason of reasonOptions; track reason) {
              <p-button
                [label]="reason"
                size="small"
                [severity]="statusEventForm.controls.reason.value === reason ? 'primary' : 'secondary'"
                (onClick)="setReason(reason)"></p-button>
            }
          </div>
        </div>
      }
    </div>

    <div class="flex gap-2 justify-end mt-4">
      <p-button label="Cancel" severity="secondary" (onClick)="statusEventDialogVisible.set(false)"></p-button>
      <p-button label="Emit event" icon="pi pi-bolt" (onClick)="emitStatusEvent()"></p-button>
    </div>
  }
</p-dialog>
<p-dialog
  header="NYC application override"
  [modal]="true"
  [visible]="nycApplicationOverrideDialogVisible()"
  (visibleChange)="nycApplicationOverrideDialogVisible.set($event)"
  [style]="{ width: '40rem' }">
  @if (nycApplicationOverrideApplication(); as application) {
    <p class="text-sm text-orange-600 mb-3">
      Test env only. This override is used when syncing application {{ application.id }}.
    </p>
    <div class="flex flex-col gap-3">
      <div class="flex flex-col gap-2">
        <span class="text-sm font-semibold">Homey application status</span>
        <div class="flex flex-wrap gap-2">
          @for (status of nycApplicationOverrideStatusOptions; track status) {
            <p-button
              [label]="status"
              size="small"
              [severity]="nycApplicationOverrideHomeyStatus() === status ? 'primary' : 'secondary'"
              (onClick)="nycApplicationOverrideHomeyStatus.set(status)"></p-button>
          }
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <label for="nycOverrideLogNr" class="text-sm font-semibold">Log number (optional)</label>
        <input
          pInputText
          id="nycOverrideLogNr"
          class="w-full"
          [value]="nycApplicationOverrideLogNumber()"
          (input)="nycApplicationOverrideLogNumber.set($any($event.target).value)"
        />
      </div>
    </div>
    @if (nycApplicationOverrideError()) {
      <p class="text-sm text-red-600 mt-2">{{ nycApplicationOverrideError() }}</p>
    }
    <div class="flex gap-2 justify-end mt-4">
      <p-button label="Cancel" severity="secondary" (onClick)="closeNycApplicationOverrideDialog()"></p-button>
      <p-button
        label="Save override"
        icon="pi pi-save"
        (onClick)="saveNycApplicationOverride()"
        [loading]="nycApplicationOverrideSaving()"
        [disabled]="nycApplicationOverrideSaving()"></p-button>
    </div>
  }
</p-dialog>
<p-dialog header="Register in NYC" [modal]="true" [(visible)]="registerInNycVisible" [style]="{ width: '50rem' }">
  <div class="flex flex-row justify-between items-center mb-6">
    <span class="p-text-secondary block">
      NYC Profile Information
      <span>
        @if (editingNycProfile) {
          <span>(Save before submitting on NYC website)</span>
        }
      </span>
    </span>
    <p-button [icon]="'pi ' + (editingNycProfile ? 'pi-save' : 'pi-pencil') "
              (onClick)="onNycProfileEditToggle()"></p-button>
  </div>
  @if (nycProfileForm(); as form) {
    <div class="flex items-center gap-4 mb-8">
      <label for="username" class="font-semibold w-24">Username</label>
      <input pInputText
             id="username"
             class="flex-auto"
             autocomplete="off"
             [formControl]="form.controls.username"/>
      <app-copy-button [value]="form.controls.username.getRawValue()"/>
    </div>
    <div class="flex items-center gap-4 mb-8">
      <label for="password" class="font-semibold w-24">Password</label>
      <input pInputText
             id="password"
             class="flex-auto"
             autocomplete="off"
             [formControl]="form.controls.password"/>
      <app-copy-button [value]="form.controls.password.getRawValue()"/>
    </div>
    <div class="flex items-center gap-4 mb-8">
      <label for="firstName" class="font-semibold w-24">First name</label>
      <input pInputText
             id="firstName"
             class="flex-auto"
             autocomplete="off"
             [disabled]="true"
             [formControl]="form.controls.firstName"/>
      <app-copy-button [value]="form.controls.firstName.getRawValue()"/>
    </div>
    <div class="flex items-center gap-4 mb-8">
      <label for="lastName" class="font-semibold w-24">Last name</label>
      <input pInputText
             id="lastName"
             class="flex-auto"
             autocomplete="off"
             [disabled]="true"
             [formControl]="form.controls.lastName"/>
      <app-copy-button [value]="form.controls.lastName.getRawValue()"/>
    </div>
    <div class="flex items-center gap-4 mb-4">
      <label for="email1" class="font-semibold w-24">Email 1</label>
      <input
        pInputText
        id="email1"
        class="flex-auto"
        autocomplete="off"
        [formControl]="form.controls.email1"
      />
      <app-copy-button [value]="form.controls.email1.getRawValue()"/>
    </div>
    <div class="flex items-center gap-4 mb-8">
      <label for="email2" class="font-semibold w-24">Email 2</label>
      <input pInputText
             id="email2"
             class="flex-auto"
             autocomplete="off"
             [formControl]="form.controls.email2"/>
      <app-copy-button [value]="form.controls.email2.getRawValue()"/>
    </div>
    <div class="flex justify-end gap-2">
      <p-button [disabled]="editingNycProfile" label="Cancel" severity="secondary"
                (click)="registerInNycVisible.set(false)"/>
      <p-button [disabled]="editingNycProfile" label="Mark as registered" (click)="onMarkAsRegisteredClick()"/>
    </div>
  }
</p-dialog>
<p-dialog
  header="Select ignore reason"
  [modal]="true"
  [visible]="ignoredReasonDialogVisible()"
  (visibleChange)="ignoredReasonDialogVisible.set($event)"
  [style]="{ width: '28rem' }">
  <p class="text-sm text-muted-color mb-4">
    Pick the reason that best matches why this user should be ignored.
  </p>
  <div class="flex flex-wrap gap-2">
    @for (option of ignoredReasonOptions; track option.value) {
      <p-button
        [label]="option.label"
        size="small"
        [severity]="pendingIgnoredReason() === option.value ? 'primary' : 'secondary'"
        (onClick)="pendingIgnoredReason.set(option.value)"></p-button>
    }
  </div>
  <div class="flex justify-end gap-2 mt-4">
    <p-button label="Cancel" severity="secondary" (onClick)="cancelIgnoredReason()"></p-button>
    <p-button
      label="Ignore user"
      icon="pi pi-check"
      [disabled]="togglingIgnore()"
      (onClick)="confirmIgnoredReason()"></p-button>
  </div>
</p-dialog>
<p-dialog
  header="Update Stripe Subscription Status"
  [modal]="true"
  [visible]="stripeStatusDialogVisible()"
  (visibleChange)="stripeStatusDialogVisible.set($event)"
  [style]="{ width: '32rem' }">
  <p class="text-sm text-muted-color mb-4">
    Select the new subscription status for this Stripe customer. This will update the payment status in the database.
  </p>
  <div class="flex flex-col gap-3">
    <div class="flex flex-wrap gap-2">
      @for (status of stripeStatusOptions; track status) {
        <p-button
          [label]="status"
          size="small"
          [severity]="selectedStripeStatus() === status ? 'primary' : 'secondary'"
          (onClick)="setStripeStatus(status)"></p-button>
      }
    </div>
  </div>
  <div class="flex gap-2 justify-end mt-4">
    <p-button label="Cancel" severity="secondary" (onClick)="stripeStatusDialogVisible.set(false)"></p-button>
    <p-button
      label="Update Status"
      icon="pi pi-check"
      [loading]="updatingStripeStatus()"
      [disabled]="updatingStripeStatus()"
      (onClick)="updateStripeStatus()"></p-button>
  </div>
</p-dialog>
